import { createApi } from '@reduxjs/toolkit/dist/query/react';
import { CacheTags, PaginatedRequest, SomeRequest } from 'types';
import { PurchaseOrderSearchRecord } from './bindings/PurchaseOrderSearchRecord';
import { PurchaseOrderResponse } from './bindings/PurchaseOrderResponse';
import { FetchResponse } from './bindings/FetchResponse';
import { OdinBaseQuery } from './core';
import { PostPurchaseOrderRequest } from './bindings/PostPurchaseOrderRequest';
import { PutPurchaseOrderRequest } from './bindings/PutPurchaseOrderRequest';
import { PurchaseOrderSearchRecordResponse } from './bindings/PurchaseOrderSearchRecordResponse';

export const PurchaseOrdersApi = createApi({
  reducerPath: 'purchaseOrders',
  baseQuery: OdinBaseQuery,
  tagTypes: [CacheTags.purchaseOrders, CacheTags.purchaseOrderDetail],
  endpoints: (builder) => ({
    postPurchaseOrder: builder.mutation<
      PurchaseOrderResponse,
      PostPurchaseOrderRequest
    >({
      query: (payload) => ({
        url: '/api/purchase-orders',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (result) => [
        { type: CacheTags.purchaseOrders, id: CacheTags.partial },
        { type: CacheTags.purchaseOrderDetail, id: result?.id },
      ],
    }),
    putPurchaseOrder: builder.mutation<
      PurchaseOrderResponse,
      PutPurchaseOrderRequest
    >({
      query: (payload) => ({
        url: `/api/purchase-orders/${payload.id}`,
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: (result, error, payload) => [
        { type: CacheTags.purchaseOrders, id: CacheTags.partial },
        { type: CacheTags.purchaseOrders, id: payload.id },
        { type: CacheTags.purchaseOrderDetail, id: payload.id },
      ],
    }),
    fetchSomePurchaseOrders: builder.query<
      FetchResponse<PurchaseOrderSearchRecord>,
      SomeRequest
    >({
      query: (payload) => ({
        url: '/api/purchase-orders',
        method: 'GET',
        params: payload,
      }),
      providesTags: (payload) => {
        if (!payload?.items) return [];
        return payload.items.map(({ id }) => ({
          type: CacheTags.purchaseOrders,
          id: id,
        }));
      },
    }),
    fetchPurchaseOrderPage: builder.query<
      FetchResponse<PurchaseOrderSearchRecordResponse>,
      PaginatedRequest
    >({
      query: (payload) => ({
        url: '/api/purchase-orders',
        method: 'GET',
        params: payload,
      }),
      providesTags: (payload) => {
        if (!payload) return [];
        return [
          { type: CacheTags.purchaseOrders, id: CacheTags.partial },
          ...payload.items.map(({ id }) => ({
            type: CacheTags.purchaseOrders,
            id: id,
          })),
        ];
      },
    }),
    fetchPurchaseOrderDetail: builder.query<PurchaseOrderResponse, string>({
      query: (payload) => ({
        url: `/api/purchase-orders/${payload}`,
        method: 'GET',
      }),
      providesTags: (payload) => {
        if (!payload) return [];
        return [
          { type: CacheTags.purchaseOrderDetail, id: payload.id },
        ];
      },
    }),
  }),
});

export const {
  useFetchSomePurchaseOrdersQuery,
  useFetchPurchaseOrderPageQuery,
  useFetchPurchaseOrderDetailQuery,
  usePostPurchaseOrderMutation,
  usePutPurchaseOrderMutation,
  // usePostPurchaseOrderTransactionMutation,
  // usePostPurchaseOrderTxnDecisionMutation,
} = PurchaseOrdersApi;
